import type { LucideIcon } from "lucide-react"
import {
  Calendar,
  Home,
  PlusCircle,
  Search,
  Sparkles,
  Ticket,
  User,
} from "lucide-react"
import type { ParseKeys, TFunction } from "i18next"

export type NavSurface = "marketing" | "app"

export type NavLinkDef = {
  path: string
  labelKey: ParseKeys
  cta?: boolean
}

export type ResolvedNavLink = {
  path: string
  label: string
  cta?: boolean
}

export type MobileTabDef = {
  key: string
  path: string
  labelKey: ParseKeys
  labelFallback?: string
  icon: LucideIcon
  match: (pathname: string) => boolean
}

/** Marketing primary links (guest marketing surfaces). */
export const MARKETING_LINKS: NavLinkDef[] = [
  { path: "/explore", labelKey: "landing.nav.explore" },
  { path: "/tap-in", labelKey: "landing.nav.tapIn" },
  { path: "/roadmap", labelKey: "landing.nav.roadmap" },
  { path: "/ecosystem", labelKey: "landing.nav.ecosystem" },
]

/** App public primary links (desktop + shared with tabs). */
export const APP_PUBLIC_LINKS: NavLinkDef[] = [
  { path: "/", labelKey: "nav.home" },
  { path: "/calendar", labelKey: "nav.calendar" },
  { path: "/explore", labelKey: "pages.explore" },
]

/** App auth-only desktop primary links. */
export const APP_AUTH_LINKS: NavLinkDef[] = [
  { path: "/create", labelKey: "nav.createEvent", cta: true },
  { path: "/my-events", labelKey: "nav.myEvents" },
]

/** Auth mobile overflow sheet only — never primary tab destinations. */
export const OVERFLOW_LINKS: NavLinkDef[] = [
  { path: "/profile", labelKey: "nav.profile" },
  { path: "/profile/notifications", labelKey: "pages.notifications" },
]

function resolveLinks(defs: NavLinkDef[], t: TFunction): ResolvedNavLink[] {
  return defs.map((def) => ({
    path: def.path,
    label: t(def.labelKey),
    cta: def.cta,
  }))
}

export function getDesktopLinks(
  authenticated: boolean,
  surface: NavSurface,
  t: TFunction,
): ResolvedNavLink[] {
  if (surface === "marketing") {
    return resolveLinks(MARKETING_LINKS, t)
  }
  const publicLinks = resolveLinks(APP_PUBLIC_LINKS, t)
  if (!authenticated) return publicLinks
  return [...publicLinks, ...resolveLinks(APP_AUTH_LINKS, t)]
}

export function getOverflowLinks(t: TFunction): ResolvedNavLink[] {
  return resolveLinks(OVERFLOW_LINKS, t)
}

const AUTH_MOBILE_TABS: MobileTabDef[] = [
  {
    key: "home",
    path: "/",
    labelKey: "nav.home",
    icon: Home,
    match: (p) => p === "/",
  },
  {
    key: "calendar",
    path: "/calendar",
    labelKey: "nav.calendar",
    icon: Calendar,
    match: (p) => p.startsWith("/calendar"),
  },
  {
    key: "explore",
    path: "/explore",
    labelKey: "nav.explore",
    labelFallback: "Explore",
    icon: Search,
    match: (p) => p.startsWith("/explore"),
  },
  {
    key: "create",
    path: "/create",
    labelKey: "nav.create",
    labelFallback: "Create",
    icon: PlusCircle,
    match: (p) => p.startsWith("/create"),
  },
  {
    key: "my-events",
    path: "/my-events",
    labelKey: "nav.myEvents",
    icon: Ticket,
    match: (p) => p.startsWith("/my-events"),
  },
]

const GUEST_MOBILE_TABS: MobileTabDef[] = [
  {
    key: "home",
    path: "/",
    labelKey: "nav.home",
    icon: Home,
    match: (p) => p === "/",
  },
  {
    key: "calendar",
    path: "/calendar",
    labelKey: "nav.calendar",
    icon: Calendar,
    match: (p) => p.startsWith("/calendar"),
  },
  {
    key: "explore",
    path: "/explore",
    labelKey: "nav.explore",
    labelFallback: "Explore",
    icon: Search,
    match: (p) => p.startsWith("/explore"),
  },
  {
    key: "tap-in",
    path: "/tap-in",
    labelKey: "nav.tapIn",
    labelFallback: "Tap In",
    icon: Sparkles,
    match: (p) => p.startsWith("/tap-in"),
  },
  {
    key: "account",
    path: "/auth",
    labelKey: "nav.account",
    labelFallback: "Account",
    icon: User,
    match: (p) => p.startsWith("/profile") || p === "/auth",
  },
]

export function getMobileTabItems(authenticated: boolean): MobileTabDef[] {
  if (authenticated) return AUTH_MOBILE_TABS
  return GUEST_MOBILE_TABS.map((tab) =>
    tab.key === "account" ? { ...tab, path: "/auth" } : tab,
  )
}
